import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { useCapability } from "@/hooks/useCapability";
import { TierGate } from "@/components/ui/TierGate";
import { MODULE_CONFIG, type ToggleableModule } from "@/lib/tier-config";

interface CapabilityGateProps {
  capability: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function CapabilityGate({ capability, children, fallback = null }: CapabilityGateProps) {
  const { available } = useCapability(capability);
  return <>{available ? children : fallback}</>;
}

interface CapabilityRouteGateProps {
  capability: string;
  children: ReactNode;
  toggledOffRedirect?: string;
}

export function CapabilityRouteGate({
  capability,
  children,
  toggledOffRedirect = "/",
}: CapabilityRouteGateProps) {
  const { available, blockedBy, reason, isLoading } = useCapability(capability);

  if (isLoading) {
    return <ForgePageLoader />;
  }

  if (available) return <>{children}</>;

  if (blockedBy === "tier") {
    // TierGate is keyed by ToggleableModule and indexes MODULE_CONFIG, so it can only
    // be used for real modules. Sub-feature keys (e.g. "accounting.advanced") are NOT
    // ToggleableModules and would crash TierGate — render a safe upgrade message instead.
    if (capability in MODULE_CONFIG) {
      // children passed only to satisfy TierGate's prop type; in the blocked case TierGate
      // renders its own upgrade card and discards children.
      return <TierGate module={capability as ToggleableModule}>{children}</TierGate>;
    }
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        {reason ?? "This feature requires a plan upgrade."}
      </div>
    );
  }

  return <Navigate to={toggledOffRedirect} replace />;
}
